import { useSplitEnabled } from '@buffer-mono/features'
import {
  Flex,
  Heading,
  Paragraph,
  Separator,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import getErrorBoundary from '~publish/components/ErrorBoundary'
import { useSelectedChannel } from '~publish/pages/Channel/ChannelContext'
import { canPerform } from '~publish/utils/permissions/canPerform'

import { GeneratePostingTimes } from './components/GeneratePostingTimes'
import { MatchPostingTimesWithGoal } from './components/MatchPostingTimesWithGoal'
import { PostingGoals } from './components/PostingGoals'
import { PostingTimeForm } from './components/PostingTimeForm'
import { ScheduleTable } from './components/ScheduleTable'
import { TimezoneInputForm } from './components/TimezoneInputForm'

import styles from './PostingSchedule.module.css'

import type { JSX } from 'react'

const ErrorBoundary = getErrorBoundary(true)

const PostingSchedule = (): JSX.Element => {
  const { isEnabled: isGeneratePostingTimesEnabled } = useSplitEnabled(
    'geid-set-my-posting-schedule',
  )
  const channel = useSelectedChannel()
  // UX gate only; the mutation is authorized server-side either way.
  const hasEditingPrivilege = canPerform(channel, 'managePostingSchedule')

  return (
    <ErrorBoundary>
      <Flex direction="column" data-testid="posting-schedule-wrapper">
        <VisuallyHidden as="h2">Posting Schedule</VisuallyHidden>
        <TimezoneInputForm />
        <Separator />
        <PostingGoals />
        <Separator />
        <Flex align="normal" direction="column" gap="lg" fullWidth>
          <Flex
            align="start"
            justify="between"
            gap="sm"
            fullWidth
            className={styles.sectionLayout}
          >
            <Flex direction="column">
              <Heading size="small" as="h3">
                Posting Slots
              </Heading>
              <Paragraph className={styles.postingScheduleParagraph}>
                Your posting times tells Buffer when to send out posts in your
                Queue. For example, the next 5 posts you add to your Queue will
                be sent in the next 5 upcoming time slots you choose below.
              </Paragraph>
            </Flex>
            {hasEditingPrivilege &&
              (isGeneratePostingTimesEnabled ? (
                <GeneratePostingTimes />
              ) : (
                <MatchPostingTimesWithGoal />
              ))}
          </Flex>
          <ScheduleTable />
          <PostingTimeForm />
        </Flex>
      </Flex>
    </ErrorBoundary>
  )
}

export { PostingSchedule }
